import React from 'react';
import Link from 'components/link';
import ImageLoader from 'components/image-loader';
import InstagramIcon from 'components/icon/instagram-icon';
import LinkedInIcon from 'components/icon/linkedin-icon';
import styles from './commercialFooterStyle.module.scss';
import Youtube from '@mui/icons-material/YouTube';
import Facebook from '@mui/icons-material/FacebookOutlined';
import { EXP_COMMERCIAL_TENANT_ID, FooterColItem, FooterConfig, OrganizationConfig } from '@tenants/types/tenant';
import { TFunction } from 'i18next';
import { EXP_COMMERCIAL_ROOT_DOMAIN, getRootDomainUrl } from 'constants/domains';
import { useTranslation } from 'next-i18next';

export type ExpCommercialFooterProps = {
  config: FooterConfig;
  organizationConfig: OrganizationConfig;
};

const Links = ({ items, keyPrefix, t }: { items: ReadonlyArray<FooterColItem>; keyPrefix: string; t: TFunction }) => {
  return (
    <div className={styles['footer-links']}>
      {
        items.map(({ isAnchor, link, target, label }, index) => {
          return (isAnchor ?
            <a key={`${keyPrefix}-${index}`} href={link} target={target}>{t(label.key, { defaultValue: label.defaultValue })}</a>
            :
            <Link key={`${keyPrefix}-${index}`} href={link} target={target}>{t(label.key, { defaultValue: label.defaultValue })}</Link>);
        })
      }
    </div>
  );
};

const BottomLinks = ({ items, keyPrefix, t }: { items: ReadonlyArray<FooterColItem>; keyPrefix: string; t: TFunction }) => {
  return (
    <>
      {items.map(({ isAnchor, link, target, label }, index) => {
        return (isAnchor ? <a key={`${keyPrefix}-${index}`} href={link} target={target}>{t(label.key, { defaultValue: label.defaultValue })}</a> : <span key={`${keyPrefix}-${index}`}>{t(label.key, { defaultValue: label.defaultValue })}</span>);
      })}
    </>
  );
};

const Logo = () => {
  return (
    <div className={styles['footer-logo-container']}>
      <ImageLoader
        className={styles['footer-logo']}
        src="/next/assets/images/exp-commercial/eXp_Commercial_logo.png"
        alt="eXp Commercial Logo"
        width="87"
        height="45"
      />
      <p className={styles['company-description']}>
        eXp Commercial is an AGNT, Inc. (Nasdaq: AGNT) company.
      </p>
    </div>
  );
};

// TODO: remove this when we go live with expCommercial
export default function ExpCommercialFooter({ config, organizationConfig }: ExpCommercialFooterProps) {
  const { t } = useTranslation(EXP_COMMERCIAL_TENANT_ID);

  const { facebookUrl, linkedinUrl, instagramUrl, youtubeUrl } = organizationConfig;
  const { column1 = [], column2 = [], column3 = [], bottom = []} = config.footerColumns;

  // TODO: remove this when we go live with expCommercial
  const rootDomainUrl = getRootDomainUrl(EXP_COMMERCIAL_ROOT_DOMAIN);
  const forceExpCommercialRootDomain = (items: ReadonlyArray<FooterColItem>) => {
    return items.map(item => {
      if (item.link && !item.link.startsWith('https://')) {
        item.link = `${rootDomainUrl}${item.link}`;
      }
      return item;
    });
  };

  return (
    <footer className={styles.footer} data-testid="exp-commercial-footer-legacy">
      <div className={styles['footer-top']}>
        <Logo />
        <div className={styles['footer-links-container']}>
          <Links items={forceExpCommercialRootDomain(column1)} keyPrefix="column1" t={t} />
          <Links items={forceExpCommercialRootDomain(column2)} keyPrefix="column2" t={t} />
        </div>
        <div className={styles['social-column']}>
          <div className={styles.socials}>
            <a href={facebookUrl} target="_blank" rel="noreferrer"><Facebook className={styles['social-x-icon']}/></a>
            <a href={linkedinUrl} target="_blank" rel="noreferrer"><LinkedInIcon isExpTheme className={styles['social-x-icon']}/></a>
            <a href={instagramUrl} target="_blank" rel="noreferrer"><InstagramIcon isExpTheme className={styles['social-ig-icon']}/></a>
            <a href={youtubeUrl} target="_blank" rel="noreferrer"><Youtube className={styles['social-ig-icon']}/></a>
          </div>
          <ExpDynamicCopyright />
        </div>
      </div>

      {/* FOOTER MIDDLE SECTION */}

      <div className={styles['footer-middle']}>
        <div className={styles['footer-disclaimers']}>
          <h4>Realty:</h4>
          {bottom.map((section, index) => {
            return (
              <p key={index}>
                <BottomLinks items={section} keyPrefix={'realty'} t={t} />
              </p>
            );
          })}
          <h4>Commercial:</h4>
          <p>eXp Commercial of California, Inc. | CA DRE # 02398654 | Timothy Rios - Designated Broker</p>
          <p>Clifford Bogart - Designated Broker - TX</p>
          {bottom.slice(0, 2).map((section, index) => (
            <p key={index}>
              <BottomLinks items={section} keyPrefix={'commercial'} t={t} />
            </p>
          ))}
        </div>
      </div>
      <div className={styles['footer-bottom']}>
        <Links items={column3} keyPrefix="column3" t={t} />
      </div>
    </footer>
  );
}

const ExpDynamicCopyright = () => (
  <p>© {new Date().getFullYear()} AGNT, Inc. All Rights Reserved</p>
);
